import { useEffect, useState } from 'react';
import { isNil } from 'lodash';
// Import Types
import type { AwardById } from 'types';
// Import Services
import { BonusHttpService } from 'services/http';
// Import Hooks
import useRequest from './UseRequest';

const bonusCache = new Map<string, AwardById>();
const inFlightRequests = new Map<string, Promise<Nullable<AwardById>>>();

interface UseBonusByIdResult {
    bonusData: Nullable<AwardById>;
    isLoading: boolean;
}

const useBonusById = (bonusId: Nilable<number | string>): UseBonusByIdResult => {
    const { doGetRequest } = useRequest();

    const cacheKey = isNil(bonusId) ? '' : String(bonusId);

    const [bonusData, setBonusData] = useState<Nullable<AwardById>>(() => bonusCache.get(cacheKey) ?? null);
    const [isLoading, setIsLoading] = useState(() => !isNil(bonusId) && !bonusCache.has(cacheKey));

    useEffect(() => {
        if (isNil(bonusId)) {
            setBonusData(null);
            setIsLoading(false);
            return;
        }

        const key = String(bonusId);

        const cached = bonusCache.get(key);
        if (!isNil(cached)) {
            setBonusData(cached);
            setIsLoading(false);
            return;
        }

        let isActive = true;
        setIsLoading(true);

        let request = inFlightRequests.get(key);
        if (isNil(request)) {
            const bonusRequest = BonusHttpService.getBonusById();
            request = doGetRequest(bonusRequest.request, { queryString: { bonusId } })
                .then((response) => {
                    const data = (response?.HasError ? null : (response?.Data ?? null)) as Nullable<AwardById>;
                    if (!isNil(data)) {
                        bonusCache.set(key, data);
                    }
                    return data;
                })
                .finally(() => {
                    inFlightRequests.delete(key);
                });
            inFlightRequests.set(key, request);
        }

        request.then((data) => {
            if (isActive) {
                setBonusData(data);
                setIsLoading(false);
            }
        });

        return () => {
            isActive = false;
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bonusId]);

    return { bonusData, isLoading };
};

export default useBonusById;
